import type { Session, User } from "@supabase/supabase-js";
import { activeSupabaseUrl } from "@/integrations/supabase/admin-client";

export const CRM_SESSION_STORAGE_KEY = "ll_crm_session";

type CrmSessionInput = {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  expires_at?: number;
  token_type?: string;
};

type StoredCrmSession = {
  session: Session;
  user: User;
  isAdmin: true;
  saved_at: number;
};

const getExpiryFromJwt = (token: string) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1] ?? ""));
    return typeof payload.exp === "number" ? payload.exp : undefined;
  } catch {
    return undefined;
  }
};

export const normalizeCrmSession = (session: CrmSessionInput, user: User): Session => {
  const expiresAt = session.expires_at ?? getExpiryFromJwt(session.access_token);

  return {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_in: session.expires_in ?? Math.max((expiresAt ?? 0) - Math.floor(Date.now() / 1000), 0),
    expires_at: expiresAt,
    token_type: session.token_type ?? "bearer",
    user,
  } as Session;
};

export const saveCrmSession = (session: CrmSessionInput, user: User) => {
  const normalized = normalizeCrmSession(session, user);
  const payload: StoredCrmSession = {
    session: normalized,
    user,
    isAdmin: true,
    saved_at: Date.now(),
  };
  localStorage.setItem(CRM_SESSION_STORAGE_KEY, JSON.stringify(payload));
  return normalized;
};

export const getStoredCrmSession = () => {
  const raw = localStorage.getItem(CRM_SESSION_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredCrmSession;
    if (!parsed?.session?.access_token || !parsed?.session?.refresh_token || !parsed?.user?.id) {
      localStorage.removeItem(CRM_SESSION_STORAGE_KEY);
      return null;
    }

    const expiresAt = parsed.session.expires_at ?? getExpiryFromJwt(parsed.session.access_token);
    if (expiresAt && expiresAt <= Math.floor(Date.now() / 1000)) {
      localStorage.removeItem(CRM_SESSION_STORAGE_KEY);
      return null;
    }

    return { session: { ...parsed.session, expires_at: expiresAt }, user: parsed.user };
  } catch {
    localStorage.removeItem(CRM_SESSION_STORAGE_KEY);
    return null;
  }
};

export const clearStoredCrmSession = () => {
  localStorage.removeItem(CRM_SESSION_STORAGE_KEY);
};

export const getStoredCrmAccessToken = () => getStoredCrmSession()?.session.access_token ?? null;

export const crmFunctionUrl = (name: string) => {
  return `${activeSupabaseUrl}/functions/v1/${name}`;
};

export const postCrmFunction = async <T>(name: string, payload: Record<string, unknown>): Promise<T> => {
  const response = await fetch(crmFunctionUrl(name), {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
    body: JSON.stringify(payload),
    credentials: "omit",
  });

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  if (!response.ok) throw new Error(data?.error ?? "No se pudo conectar con el CRM");
  return data as T;
};